import React, { useEffect, useState } from 'react';
import { ensureAuthorization, getAccessToken } from '../auth/index.js';
import { getApolloClient } from '../client.js';
import { getConfig } from '../config/index.js';
import { getTemplate } from '../getTemplate.js';
import { SEED_QUERY } from '../queries/seedQuery.js';
import { getQueryParam } from '../utils/convert.js';
export function WordPressTemplate(props) {
    const { templates } = getConfig();
    if (!templates) {
        throw new Error('Templates are required. Please add them to your config.');
    }
    const { __SEED_NODE__: seedNodeProp, __TEMPLATE_QUERY_DATA__: templateQueryDataProp, } = props;
    const [seedNode, setSeedNode] = useState(seedNodeProp);
    const template = getTemplate(seedNode, templates);
    const [data, setData] = useState(templateQueryDataProp);
    const [loading, setLoading] = useState(template === null);
    const [isPreview, setIsPreview] = useState(templateQueryDataProp ? false : null);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    /**
     * Determine if the URL we are on is for previews
     */
    useEffect(() => {
        if (!window) {
            return;
        }
        setIsPreview(window.location.search.includes('preview=true'));
    }, []);
    /**
     * If the URL we are on is for previews, ensure we are authenticated.
     */
    useEffect(() => {
        if (isPreview === null || isPreview === false) {
            return;
        }
        void (async () => {
            const ensureAuthRes = await ensureAuthorization({
                redirectUri: window.location.href,
            });
            if (ensureAuthRes !== true && (ensureAuthRes === null || ensureAuthRes === void 0 ? void 0 : ensureAuthRes.redirect)) {
                window.location.replace(ensureAuthRes.redirect);
            }
            setIsAuthenticated(ensureAuthRes);
        })();
    }, [isPreview]);
    /**
     * Execute the seed query.
     *
     * If the seed query was not available via a prop, it was not executed on the
     * server, meaning we are either dealing with a CSR page, or a preview page.
     */
    useEffect(() => {
        if (isPreview === null) {
            return;
        }
        if (isPreview === true && isAuthenticated !== true) {
            return;
        }
        void (async () => {
            var _a;
            const client = getApolloClient();
            let seedQueryUri = window.location.href.replace(window.location.origin, '');
            if (isPreview) {
                seedQueryUri = getQueryParam(window.location.href, 'previewPathname');
                if (seedQueryUri === '') {
                    throw new Error('The URL must contain the proper "previewPathname" query param for previews.');
                }
            }
            let queryArgs = {
                query: SEED_QUERY,
                variables: {
                    uri: seedQueryUri,
                },
            };
            if (isPreview) {
                queryArgs = Object.assign(Object.assign({}, queryArgs), { context: {
                        headers: {
                            /**
                             * We know the access token is available here since we ensured
                             * authorization in the useEffect above
                             */
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            Authorization: `bearer ${getAccessToken()}`,
                        },
                    } });
            }
            if (!seedNode) {
                setLoading(true);
                const seedQueryRes = await client.query(queryArgs);
                const node = (_a = seedQueryRes === null || seedQueryRes === void 0 ? void 0 : seedQueryRes.data) === null || _a === void 0 ? void 0 : _a.node;
                setSeedNode(node);
            }
        })();
    }, [seedNode, isPreview, isAuthenticated]);
    /**
     * Finally, get the template's query data.
     */
    useEffect(() => {
        // We don't know yet if this is a preview route or not
        if (isPreview === null) {
            return;
        }
        // This is a preview route, but we are not authenticated yet.
        if (isPreview === true && isAuthenticated !== true) {
            return;
        }
        void (async () => {
            const client = getApolloClient();
            if (!template || !(template === null || template === void 0 ? void 0 : template.query) || !seedNode) {
                return;
            }
            if (!data) {
                setLoading(true);
                let queryArgs = {
                    query: template === null || template === void 0 ? void 0 : template.query,
                    variables: (template === null || template === void 0 ? void 0 : template.variables)
                        ? template === null || template === void 0 ? void 0 : template.variables(seedNode, { asPreview: isPreview })
                        : undefined,
                };
                if (isPreview) {
                    queryArgs = Object.assign(Object.assign({}, queryArgs), { context: {
                            headers: {
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                Authorization: `bearer ${getAccessToken()}`,
                            },
                        } });
                }
                const templateQueryRes = await client.query(queryArgs);
                setData(templateQueryRes.data);
                setLoading(false);
            }
        })();
    }, [data, template, seedNode, isPreview, isAuthenticated]);
    if (!template) {
        return null;
    }
    const Component = template;
    return React.createElement(Component, Object.assign(Object.assign({}, props), { data, loading }), null);
}
